import { api } from './client'

// ============================================================
// AWS Role Coverage — client de API (Task 13)
// Rotas canônicas: /api/v1/admin/aws-access/coverage(+/refresh,/provision,/provision/{jobId})
// Requer permissão aws_access:read (leitura) / aws_access:provision (mutações).
// ============================================================

/** Status de cobertura de uma conta/role detectado pelo scanner.
 * Fonte: aws_role_coverage.py — detect_role produz ok|missing|trust_broken|denied|error;
 * run_sweep adiciona not_applicable para contas payer.
 */
export type CoverageStatus =
  | 'ok'
  | 'missing'
  | 'trust_broken'
  | 'denied'
  | 'error'
  | 'not_applicable'

/** Método pelo qual o status foi determinado. */
export type DetectionMethod = 'assume_role' | 'get_role' | 'payer'

/**
 * Status de validação de actions IAM dentro de uma role FinOps.
 * Retornado opcionalmente pelo backend quando a role é FinOps e o scanner
 * realizou a validação de actions.
 */
export type ActionsStatus =
  | 'complete'
  | 'actions_missing'
  | 'not_checked'
  | 'validation_unavailable'

/**
 * Status de ativação da conta no ConsoleAI (co_status).
 * Indica se a conta/tenant está ativa, inativa, pendente ou com falha.
 */
export type CoStatus = 'active' | 'inactive' | 'pending' | 'failed' | 'unknown'

/**
 * Row de cobertura de acesso IAM para uma conta AWS.
 * Espelha o shape retornado por GET /api/v1/admin/aws-access/coverage.
 *
 * Os campos de prontidão FinOps (actions_status, missing_actions,
 * template_grants_missing, co_status) são OPCIONAIS — o backend pode omiti-los
 * em versões anteriores; a UI degrada graciosamente quando ausentes.
 */
export interface CoverageRow {
  account_id: string
  tenant_id: number | null
  role_name: string
  status: CoverageStatus
  detection_method: DetectionMethod | null
  detail_code: string | null
  detail_message: string | null
  last_checked_at: string | null
  tenant_name: string | null
  tenant_environment: string | null
  /** Prontidão das actions IAM (apenas roles FinOps). Omitido quando não aplicável. */
  actions_status?: ActionsStatus
  /** Lista de actions IAM faltantes (populada quando actions_status='actions_missing'). */
  missing_actions?: string[]
  /**
   * Indica se as actions faltantes precisam ser adicionadas ao template StackSet
   * (true) ou se o template já inclui e basta remediar (false). Null quando não
   * determinado.
   */
  template_grants_missing?: boolean | null
  /** Status de ativação da conta no ConsoleAI. Omitido quando desconhecido. */
  co_status?: CoStatus
}

/** Metadados do último scan executado (espelha aws_role_coverage_scan_runs). */
export interface ScanRun {
  id: number
  scope: string
  account_id: string | null
  status: string
  accounts_total: number | null
  accounts_ok: number | null
  accounts_with_gap: number | null
  started_at: string
  finished_at: string | null
  triggered_by: number | null
}

/** Status de um job de provisionamento de role (espelha aws_role_provision_jobs.status). */
export type ProvisionJobStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'failed'
  | 'noop'

/**
 * Job de provisionamento de role IAM em uma conta AWS.
 * Retornado por GET /api/v1/admin/aws-access/coverage/provision/{jobId}.
 * Espelha aws_role_provision_jobs.
 */
export interface ProvisionJob {
  id: number
  account_id: string
  role_name: string
  status: ProvisionJobStatus
  operation_id: string | null
  error: string | null
  requested_by: number | null
  created_at: string
  started_at: string | null
  finished_at: string | null
}

/**
 * Retorna a cobertura atual de acesso IAM por conta AWS.
 * Backend: GET /api/v1/admin/aws-access/coverage
 */
export function getCoverage(): Promise<{ rows: CoverageRow[]; scan_run: ScanRun | null }> {
  return api
    .get<{ rows: CoverageRow[]; scan_run: ScanRun | null }>('/v1/admin/aws-access/coverage')
    .then((r) => r.data)
}

/**
 * Uma linha do veredito por CAPACIDADE (feature), por tenant — TASK-9888.
 *
 * Vem de `/api/v1/permissions/overview`, que e alimentado pelo validador diario de permissoes
 * (`permission_validator`) — sistema DIFERENTE do que alimenta `getCoverage`. A tela junta os dois
 * por `account_id`: a cobertura responde "consigo assumir o papel?", e esta responde "o tenant
 * roda os modulos?".
 */
export interface CapacidadesDoTenant {
  tenant_name: string
  account_id: string
  status: string
  validated_at: string | null
  /** Capacidades com negacao DEFINITIVA de acao obrigatoria. */
  features_falha?: string[]
  /**
   * Capacidades que a plataforma NAO conseguiu verificar (acao inconclusiva, ou tenant nunca
   * validado). **Nao e o mesmo que estar funcionando** — e por isso e lista separada de
   * `features_falha`, e nao um estado dentro dela.
   */
  features_nao_medidas?: string[]
}

/**
 * Veredito por capacidade de todos os tenants.
 * Backend: GET /api/v1/permissions/overview
 */
export function getCapacidadesPorTenant(): Promise<CapacidadesDoTenant[]> {
  return api
    .get<{ items: CapacidadesDoTenant[] }>('/v1/permissions/overview')
    .then((r) => r.data?.items ?? [])
}

/**
 * Dispara re-scan de cobertura (opcional: filtra por conta).
 * Backend: POST /api/v1/admin/aws-access/coverage/refresh
 */
export function refreshCoverage(
  accountId?: string,
): Promise<{ status: string; scan_run?: ScanRun }> {
  return api
    .post<{ status: string; scan_run?: ScanRun }>(
      '/v1/admin/aws-access/coverage/refresh',
      accountId ? { account_id: accountId } : {},
    )
    .then((r) => r.data)
}

/**
 * Enfileira provisionamento da role IAM em uma conta AWS.
 * Backend: POST /api/v1/admin/aws-access/coverage/provision
 */
export function provisionRole(
  accountId: string,
  roleName: string,
): Promise<{ job_id: number; status: string }> {
  return api
    .post<{ job_id: number; status: string }>(
      '/v1/admin/aws-access/coverage/provision',
      { account_id: accountId, role_name: roleName },
    )
    .then((r) => r.data)
}

/**
 * Detalhe de um job de provisionamento por id.
 * Backend: GET /api/v1/admin/aws-access/coverage/provision/{jobId}
 */
export function getProvisionJob(jobId: number): Promise<ProvisionJob> {
  return api
    .get<ProvisionJob>(`/v1/admin/aws-access/coverage/provision/${jobId}`)
    .then((r) => r.data)
}

// ============================================================
// FinOps Detection Health
// Rota: GET /api/v1/admin/aws-access/coverage/finops-health
// ============================================================

/**
 * Estado geral da detecção FinOps (última execução do scanner de actions).
 *   ok           — detecção rodou e produziu dados recentes.
 *   falha        — detecção rodou mas não produziu items (errors > 0).
 *   stale        — detecção parada há muito tempo (sem runs recentes).
 *   sem_dados    — detecção ainda não reportou nenhum resultado.
 *   indisponivel — endpoint não acessível ou erro inesperado.
 */
export type FinopsHealthState =
  | 'ok'
  | 'falha'
  | 'stale'
  | 'sem_dados'
  | 'indisponivel'

/** Payload retornado por GET /api/v1/admin/aws-access/coverage/finops-health. */
export interface FinopsHealth {
  state: FinopsHealthState
  items: number | null
  errors: number | null
  assume_role_failures: number | null
  access_denied: number | null
  last_run: string | null
}

/**
 * Retorna o estado de saúde da detecção FinOps.
 * Backend: GET /api/v1/admin/aws-access/coverage/finops-health
 */
export function fetchFinopsHealth(): Promise<FinopsHealth> {
  return api
    .get<FinopsHealth>('/v1/admin/aws-access/coverage/finops-health')
    .then((r) => r.data)
}
